/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: systemMiscApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API functions for System Misc frontend module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { SystemMiscConfig, ListSystemMiscResponse, FetchSystemMiscParams } from '../types';

export async function fetchSystemMiscConfigs(
  params: FetchSystemMiscParams,
  tenantSlug: string,
  userId?: number,
): Promise<{ data: SystemMiscConfig[]; pagination: ListSystemMiscResponse['pagination'] }> {
  const searchParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  });

  if (params.search) {
    searchParams.set('search', params.search);
  }

  const response = await fetch(`${buildApiUrl('/api/system-misc')}?${searchParams.toString()}`, {
    headers: getApiHeaders(tenantSlug, userId),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch system misc configs: ${response.statusText}`);
  }

  const payload: ListSystemMiscResponse = await response.json();
  const rows: SystemMiscConfig[] = Array.isArray(payload?.data)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ? payload.data.map((item: any) => ({
        ...item,
        id: item?.id ?? item?.uuid ?? '',
        serverId: item?.serverId ?? item?.server?.id ?? null,
        server: item?.server
          ? {
              ...item.server,
              id: item.server.id ?? item.server.uuid ?? '',
            }
          : undefined,
      }))
    : [];

  return {
    data: rows,
    pagination:
      payload?.pagination ?? {
        page: params.page,
        limit: params.limit,
        total: rows.length,
        pages: 1,
      },
  };
}

export async function fetchSystemMiscQuickList(
  search?: string,
  tenantSlug?: string,
  userId?: number,
): Promise<SystemMiscConfig[]> {
  const query = search ? `?search=${encodeURIComponent(search)}` : '';
  const response = await fetch(`${buildApiUrl('/api/system-misc/quicklist')}${query}`, {
    headers: getApiHeaders(tenantSlug ?? '', userId),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch quick list: ${response.statusText}`);
  }

  const payload: ListSystemMiscResponse = await response.json();
  return Array.isArray(payload?.data)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ? payload.data.map((item: any) => ({
        ...item,
        id: item?.id ?? item?.uuid ?? '',
        server: item?.server
          ? {
              ...item.server,
              id: item.server.id ?? item.server.uuid ?? '',
            }
          : undefined,
      }))
    : [];
}

export async function refreshServerStatus(
  serverId: string | number,
  tenantSlug: string,
  userId?: number,
): Promise<{ success: boolean; data?: { serverStatus?: string } }> {
  const response = await fetch(buildApiUrl(`/api/telephony-servers/${serverId}/verify`), {
    method: 'POST',
    headers: getApiHeaders(tenantSlug, userId),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error(`Failed to refresh server status: ${response.statusText}`);
  }

  return response.json();
}

